import React, { useState, useEffect, useCallback, useMemo } from 'react';
import * as XLSX from 'xlsx';
import {
  Box,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  InputAdornment,
  IconButton,
  Drawer,
  Divider,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';

const claimOptions = [
  { code: 'E01', label: 'Educational Assistance (1st to 7th & 8th to 10th)', amount: 2500 },
  { code: 'E02', label: 'Educational Assistance (10th or 12th 50%+)', amount: 10000 },
  { code: 'E03', label: 'Educational Assistance (11th and 12th Std)', amount: 10000 },
  { code: 'E04', label: 'Educational Assistance (Degree Course)', amount: 20000 },
  { code: 'E05', label: 'Educational Assistance (Medical Degree)', amount: 100000 },
  { code: 'E06', label: 'Educational Assistance (Diploma Course)', amount: 20000 },
  { code: 'E07', label: 'Reimbursement of MS-CIT Course Fees', amount: 0 },
  { code: 'S1', label: 'Financial Assistance for First Marriage', amount: 30000 },
  { code: 'S3', label: 'Distribution of Personality Development Books', amount: 0 },
  { code: 'S4', label: 'Financial Assistance for Purchase of Tools', amount: 5000 },
  { code: 'S5', label: 'Pradhan Mantri Jeevan Jyoti Bima Yojana', amount: 0 },
  { code: 'S6', label: 'Pradhan Mantri Suraksha Bima Yojana', amount: 0 },
  { code: 'S7', label: 'Skill Development Scheme', amount: 0 },
  { code: 'H1', label: 'Financial Assistance for Delivery (Natural/Caesarean)', amount: 15000 },
  { code: 'H2', label: 'Medical Assistance for Serious Ailments', amount: 100000 },
  { code: 'H3', label: 'Fixed Deposit for Female Child (Family Planning)', amount: 100000 },
  { code: 'H4', label: 'Financial Assistance for Permanent Disability', amount: 200000 },
  { code: 'H5', label: 'Mahatma Jyotiba Phule Jan Arogya Yojana', amount: 0 },
  { code: 'F01', label: 'Financial Assistance for Accidental Death', amount: 500000 },
  { code: 'F02', label: 'Financial Assistance for Natural Death', amount: 200000 },
  { code: 'F03', label: 'Loan Subsidy for House Construction', amount: 0 },
  { code: 'F05', label: 'Financial Assistance for Funeral', amount: 10000 },
  { code: 'F06', label: 'Financial Assistance for Widow/Widower', amount: 24000 },
];

const emptyForm = {
  id: null,
  name: '',
  gender: 'Male',
  mobile: '',
  aadhar: '',
  epic_no: '',
  vidhansabha: 'Karad South',
  karad: { village: '', ganName: '', zpName: '', reference: '' },
  other: { village: '', taluka: '', district: '' },
  booth_no: '',
  formDate: '',
  ackNo: '',
  wfcDate: '',
  wfcLocation: 'Koyana Vasahat',
  wfcOtherLocation: '',
  wfcRemarks: '',
  operator: '',
  claimCode: '',
  claimAmount: '',
};

// API base - set `REACT_APP_API_BASE` in .env if needed
const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';

export default function Claim({ userid }) {
  const [records, setRecords] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    name: '',
    mobile: '',
    aadhar: '',
    village: '',
    claimCode: '',
    ackNo: '',
    operator: ''
  });

  useEffect(() => {
    fetchRecords();
  }, []);

  const fetchRecords = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/claim-records`);
      const data = await res.json();
      setRecords(data.map(mapFromApi));
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  function mapFromApi(r) {
    const isKarad = r.vidhansabha === 'Karad South';
    return {
      id: r.id,
      name: r.name || '',
      gender: r.gender || 'Male',
      mobile: r.mobile || '',
      aadhar: r.aadhar || '',
      epic_no: r.epic_no || '',
      vidhansabha: r.vidhansabha || 'Karad South',
      karad: {
        village: isKarad ? (r.village || '') : '',
        ganName: r.gan_name || '',
        zpName: r.zp_name || '',
        reference: r.reference || '',
      },
      other: {
        village: !isKarad ? (r.village || '') : '',
        taluka: r.taluka || '',
        district: r.district || '',
      },
      booth_no: r.booth_no || '',
      formDate: r.form_date || '',
      ackNo: r.ack_no || '',
      claimCode: r.claim_code || '',
      claimAmount: r.claim_amount || '',
      wfcDate: r.wfc_date || '',
      wfcLocation: r.wfc_location || 'Koyana Vasahat',
      wfcOtherLocation: r.wfc_other_location || '',
      wfcRemarks: r.wfc_remarks || '',
      operator: r.operator || '',
    };
  }

  function mapToApi(f, uid) {
    const isKarad = f.vidhansabha === 'Karad South';
    return {
      userid: uid,
      name: f.name,
      gender: f.gender,
      mobile: f.mobile,
      aadhar: f.aadhar,
      epic_no: f.epic_no,
      vidhansabha: f.vidhansabha,
      village: isKarad ? f.karad.village : f.other.village,
      booth_no: f.booth_no,
      gan_name: f.karad.ganName,
      zp_name: f.karad.zpName,
      reference: f.karad.reference,
      taluka: f.other.taluka,
      district: f.other.district,
      form_date: f.formDate || null,
      ack_no: f.ackNo,
      claim_code: f.claimCode,
      claim_amount: f.claimAmount || 0,
      wfc_date: f.wfcDate || null,
      wfc_location: f.wfcLocation,
      wfc_other_location: f.wfcOtherLocation,
      wfc_remarks: f.wfcRemarks,
      operator: f.operator,
    };
  }

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    if (name.startsWith('karad.')) {
      const k = name.split('.')[1];
      setForm((s) => ({ ...s, karad: { ...s.karad, [k]: value } }));
      return;
    }
    if (name.startsWith('other.')) {
      const k = name.split('.')[1];
      setForm((s) => ({ ...s, other: { ...s.other, [k]: value } }));
      return;
    }
    setForm((s) => ({ ...s, [name]: value }));
  }, []);

  const handleClaimChange = useCallback((e) => {
    const code = e.target.value;
    const opt = claimOptions.find((c) => c.code === code);
    setForm((s) => ({
      ...s,
      claimCode: code,
      claimAmount: opt ? opt.amount : ''
    }));
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    const method = form.id ? 'PUT' : 'POST';
    const url = form.id ? `${API_BASE}/claim-records/${form.id}` : `${API_BASE}/claim-records`;

    try {
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(mapToApi(form, userid)),
      });
      if (res.ok) {
        fetchRecords();
        resetForm();
        setDrawerOpen(false);
      } else {
        alert('Failed to save record');
      }
    } catch (err) {
      console.error('Save error:', err);
    }
  }

  const handleEdit = useCallback((id) => {
    const rec = records.find((r) => r.id === id);
    if (rec) {
      setForm({ ...rec });
      setEditing(true);
      setDrawerOpen(true);
    }
  }, [records]);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm('Delete this record?')) return;
    try {
      const res = await fetch(`${API_BASE}/claim-records/${id}`, { method: 'DELETE' });
      if (res.ok) {
        fetchRecords();
      } else {
        alert('Failed to delete');
      }
    } catch (err) {
      console.error('Delete error:', err);
    }
  }, [fetchRecords]);

  const handleFilterChange = useCallback((e) => {
    const { name, value } = e.target;
    setFilters((f) => ({ ...f, [name]: value }));
  }, []);

  const filteredRecords = useMemo(() => {
    const m = (val = '') => (val == null ? '' : String(val).toLowerCase());
    return records.filter((r) => {
      return (
        m(r.name).includes(m(filters.name)) &&
        m(r.mobile).includes(m(filters.mobile)) &&
        m(r.aadhar).includes(m(filters.aadhar)) &&
        (m(r.karad?.village).includes(m(filters.village)) || m(r.other?.village).includes(m(filters.village))) &&
        m(r.claimCode).includes(m(filters.claimCode)) &&
        m(r.ackNo).includes(m(filters.ackNo)) &&
        m(r.operator).includes(m(filters.operator))
      );
    });
  }, [records, filters]);

  const handleDownloadExcel = () => {
    const dataToExport = filteredRecords.map(r => ({
      Name: r.name,
      Gender: r.gender,
      Mobile: r.mobile,
      Aadhar: r.aadhar,
      'EPIC No': r.epic_no,
      Vidhansabha: r.vidhansabha,
      Village: r.karad?.village || r.other?.village,
      'Booth No': r.booth_no,
      'Gan Name': r.karad?.ganName,
      'ZP Name': r.karad?.zpName,
      Reference: r.karad?.reference,
      Taluka: r.other?.taluka,
      District: r.other?.district,
      'Form Date': r.formDate,
      'Ack No': r.ackNo,
      'Claim Code': r.claimCode,
      'Claim Amount': r.claimAmount,
      'WFC Date': r.wfcDate,
      'WFC Location': r.wfcLocation,
      'WFC Other Location': r.wfcOtherLocation,
      'WFC Remarks': r.wfcRemarks,
      Operator: r.operator,
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Claims');
    XLSX.writeFile(workbook, `Claims_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  function resetForm() {
    setForm(emptyForm);
    setEditing(false);
    setDrawerOpen(false);
  }

  return (
    <Box sx={{ p: 1 }}>
      <Paper sx={{ p: 2, bgcolor: 'white' }} elevation={4}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
          <Typography variant="h6" sx={{ color: '#0b4a6f', fontWeight: 'bold' }}>Claim Form Records</Typography>
          <Chip label={`${records.length} records`} color="primary" variant="outlined" size="small" />
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="outlined"
            color="success"
            onClick={handleDownloadExcel}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold', mr: 1 }}
          >
            Download Excel
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => { resetForm(); setDrawerOpen(true); }}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
          >
            Add New Claim
          </Button>
        </Box>

        {useMemo(() => (
          <TableContainer sx={{ maxHeight: 'calc(100vh - 200px)', border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {['Name', 'Mobile', 'Aadhar', 'Village', 'Claim Code', 'Amount', 'Ack No', 'Operator', 'Actions'].map((h) => (
                    <TableCell key={h} sx={{ bgcolor: '#0b4a6f', color: 'white', fontWeight: 'bold', py: 1.5 }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="name" value={filters.name} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="primary" /></InputAdornment>, disableUnderline: true }} placeholder="Search..." /></TableCell>
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="mobile" value={filters.mobile} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="aadhar" value={filters.aadhar} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="village" value={filters.village} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="claimCode" value={filters.claimCode} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }} />
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="ackNo" value={filters.ackNo} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="operator" value={filters.operator} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRecords.map((r) => (
                  <TableRow key={r.id} hover sx={{ '&:nth-of-type(even)': { bgcolor: '#fafafa' } }}>
                    <TableCell sx={{ fontWeight: 500 }}>{r.name}</TableCell>
                    <TableCell>{r.mobile}</TableCell>
                    <TableCell>{r.aadhar}</TableCell>
                    <TableCell>{r.karad?.village || r.other?.village}</TableCell>
                    <TableCell>{r.claimCode}</TableCell>
                    <TableCell>{r.claimAmount}</TableCell>
                    <TableCell>{r.ackNo}</TableCell>
                    <TableCell>{r.operator}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <IconButton size="small" color="primary" onClick={() => handleEdit(r.id)} sx={{ bgcolor: '#e3f2fd', '&:hover': { bgcolor: '#bbdefb' } }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(r.id)} sx={{ bgcolor: '#ffebee', '&:hover': { bgcolor: '#ffcdd2' } }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ), [filteredRecords, filters, handleFilterChange, handleEdit, handleDelete])}
      </Paper>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box component="form" onSubmit={handleSubmit} sx={{ width: { xs: '100vw', sm: 500 }, p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ color: '#0b4a6f', flexGrow: 1, fontWeight: 'bold' }}>
              {editing ? 'Edit Claim' : 'Add New Claim'}
            </Typography>
            <IconButton onClick={() => setDrawerOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', flexGrow: 1, pr: 1 }}>
            <TextField label="Name" name="name" value={form.name} onChange={handleChange} required fullWidth variant="outlined" />

            <FormControl fullWidth>
              <InputLabel id="gender-label">Gender</InputLabel>
              <Select labelId="gender-label" label="Gender" name="gender" value={form.gender} onChange={handleChange}>
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
              </Select>
            </FormControl>

            <TextField label="Mobile No" name="mobile" value={form.mobile} onChange={handleChange} fullWidth variant="outlined" />
            <TextField label="Aadhar No" name="aadhar" value={form.aadhar} onChange={handleChange} fullWidth variant="outlined" />
            <TextField label="MH No" name="epic_no" value={form.epic_no} onChange={handleChange} fullWidth variant="outlined" />

            <FormControl fullWidth>
              <InputLabel id="vidh-label">Vidhansabha</InputLabel>
              <Select labelId="vidh-label" label="Vidhansabha" name="vidhansabha" value={form.vidhansabha} onChange={handleChange}>
                <MenuItem value="Karad South">Karad South</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>

            {form.vidhansabha === 'Karad South' ? (
              <Box sx={{ borderLeft: '3px solid #00acc1', pl: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField label="Village" name="karad.village" value={form.karad.village} onChange={handleChange} fullWidth variant="outlined" />
                <TextField label="Booth No" name="booth_no" value={form.booth_no} onChange={handleChange} fullWidth variant="outlined" />
                <TextField label="Gan Name" name="karad.ganName" value={form.karad.ganName} onChange={handleChange} fullWidth variant="outlined" />
                <TextField label="ZP Name" name="karad.zpName" value={form.karad.zpName} onChange={handleChange} fullWidth variant="outlined" />
                <TextField label="Reference" name="karad.reference" value={form.karad.reference} onChange={handleChange} fullWidth variant="outlined" />
              </Box>
            ) : (
              <Box sx={{ borderLeft: '3px solid #8e24aa', pl: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField label="Village" name="other.village" value={form.other.village} onChange={handleChange} fullWidth variant="outlined" />
                <TextField label="Booth No" name="booth_no" value={form.booth_no} onChange={handleChange} fullWidth variant="outlined" />
                <TextField label="Taluka" name="other.taluka" value={form.other.taluka} onChange={handleChange} fullWidth variant="outlined" />
                <TextField label="District" name="other.district" value={form.other.district} onChange={handleChange} fullWidth variant="outlined" />
              </Box>
            )}

            <TextField label="Form Date" name="formDate" type="date" InputLabelProps={{ shrink: true }} value={form.formDate} onChange={handleChange} fullWidth variant="outlined" />
            <TextField label="Acknowledgement No." name="ackNo" value={form.ackNo} onChange={handleChange} fullWidth variant="outlined" />

            <FormControl fullWidth>
              <InputLabel id="claim-code-label">Claim Code</InputLabel>
              <Select labelId="claim-code-label" label="Claim Code" name="claimCode" value={form.claimCode} onChange={handleClaimChange}>
                <MenuItem value="">-- Select --</MenuItem>
                {claimOptions.map((c) => (
                  <MenuItem key={c.code} value={c.code}>{`${c.code} — ${c.label}`}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField label="Claim Amount" name="claimAmount" value={form.claimAmount} onChange={handleChange} type="number" fullWidth variant="outlined" helperText="You can put amount manually if needed" />

            <TextField label="WFC Visit Date" name="wfcDate" type="date" InputLabelProps={{ shrink: true }} value={form.wfcDate} onChange={handleChange} fullWidth variant="outlined" />

            <FormControl fullWidth>
              <InputLabel id="wfc-loc-label">WFC Visit Location</InputLabel>
              <Select labelId="wfc-loc-label" label="WFC Visit Location" name="wfcLocation" value={form.wfcLocation} onChange={handleChange}>
                <MenuItem value="Koyana Vasahat">Koyana Vasahat</MenuItem>
                <MenuItem value="Banawadi">Banawadi</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>

            {form.wfcLocation === 'Other' && (
              <TextField label="Other WFC Center (Manually)" name="wfcOtherLocation" value={form.wfcOtherLocation} onChange={handleChange} fullWidth variant="outlined" />
            )}

            <TextField label="WFC Visit Remarks" name="wfcRemarks" value={form.wfcRemarks} onChange={handleChange} multiline rows={3} fullWidth variant="outlined" />

            <TextField label="Operator Name" name="operator" value={form.operator} onChange={handleChange} fullWidth variant="outlined" />
          </Box>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={2}>
            <Button type="submit" variant="contained" color="primary" fullWidth size="large" sx={{ borderRadius: 2 }}>
              {editing ? 'Update' : 'Save'}
            </Button>
            <Button variant="outlined" onClick={resetForm} fullWidth size="large" sx={{ borderRadius: 2 }}>
              Reset
            </Button>
          </Stack>
        </Box>
      </Drawer>
    </Box>
  );
}
